import {
    useEffect,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type IDataTableToolbarProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDataTableSelectionBarContext,
    useDataTableContext
} from "../../context";

const DataTableToolbar: FC<IDataTableToolbarProps> = ({
    customTheme: customThemeProp,
    children,
    style,
    ...props
}) => {
    const context = useDataTableContext({
        part: "Toolbar"
    });

    const selectionBarContext = useDataTableSelectionBarContext();

    const {
        controlHeights,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        container: containerDynamicStyle
    } = useStyles({
        controlHeights,
        spaces
    });

    const containerRef = useRef<View | null>(null);

    const setToolbarLayout = selectionBarContext?.setToolbarLayout;

    const isSelectionBarActive = !!selectionBarContext?.isSelectionBarActive;

    useEffect(() => {
        return () => {
            setToolbarLayout?.(undefined);
        };
    }, [
        setToolbarLayout
    ]);

    useEffect(() => {
        const node = containerRef.current as unknown as HTMLElement | null;

        if(Platform.OS !== "web" || !node) {
            return;
        }

        if(isSelectionBarActive) {
            node.setAttribute("inert", "");

            return;
        }

        node.removeAttribute("inert");
    }, [
        isSelectionBarActive
    ]);

    const handleLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            y
        } = event.nativeEvent.layout;

        setToolbarLayout?.({
            height,
            y
        });

        props.onLayout?.(event);
    };

    if(!context) {
        return null;
    }

    return <Animated.View
        aria-label={localize("data-table-toolbar")}
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            {
                pointerEvents: isSelectionBarActive ? "none" : "auto"
            },
            selectionBarContext ? {
                opacity: selectionBarContext.selectionBarProgress.interpolate({
                    outputRange: [
                        1,
                        0
                    ],
                    inputRange: [
                        0,
                        1
                    ]
                })
            } : null
        ]}
        aria-hidden={isSelectionBarActive}
        onLayout={handleLayout}
        ref={containerRef}
        role="group"
    >
        {children}
    </Animated.View>;
};
export default DataTableToolbar;
